(ns app.front.ui.password
  (:require [app.front.util :as futil]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [>evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-event-fx ::request-score
  (fn [_ [_ callback password]]
    {:comm/command {:command    :user/password-strength
                    :data       {:user/password password}
                    :on-success [::request-score.success callback]}}))


(rf/reg-event-fx ::request-score-debounce
  (fn [_ [_ callback password]]
    {:dispatch-debounce [{:id      ::request-score
                          :timeout 300
                          :action  :dispatch
                          :event   [::request-score callback password]}]}))


(def default-password-score
  {:type :weak :name :password.score/weak :strength 1 :class "text-support-01" :icon i/warning})


(defn- format-result [{:keys [score warning suggestions]}]
  (let [str->tr-key (partial keyword "password.feedback")]
    {:score       (case score
                    -1 {:type :not-applicable :name :password.score/not-applicable :class "text-support-01" :icon i/warning}
                    4 {:type :good :name :password.score/strong :strength 3 :class "text-support-02" :icon i/check}
                    3 {:type :moderate :name :password.score/moderate :strength 2 :class "text-support-03" :icon i/check}
                    default-password-score)
     :warning     (when-not (str/blank? warning) (str->tr-key warning))
     :suggestions (->> suggestions
                       (map str->tr-key))}))


(rf/reg-event-fx ::request-score.success
  (fn [_ [_ cb _ result]]
    (cb (format-result result))))


(defn calculate-score [password cb]
  (if-not (str/blank? password)
    (>evt [::request-score-debounce cb password])
    (cb nil)))


(defn score-panel [tr {:keys [score warning suggestions]}]
  (let [hidden     (nil? score)
        score      (or score default-password-score)
        use-meter? (-> score :strength some?)]
    [:div.flex.flex-col.items-start.w-full {:style {:order 3} :class (when hidden "invisible")}
     (when use-meter?
       [:meter.password-strength-meter.w-full.h-2.mt-0.mb-2.mx-auto
        {:min 0 :max 3 :value (:strength score)}
        (tr [(:name score)])])
     [:div.flex.items-start.text-xs.leading-normal {:class [(:class score)
                                                            (when-not use-meter? "mt-2")]}
      [cc/icon {:icon        (:icon score)
                :description (tr [(:name score)])
                :class-name  "fill-current mr-2"}]
      [:div.flex.flex-col
       [:div (tr [(:name score)])]
       (when (some? warning)
         [:div.text-ui-night-sky.text-xs.leading-tight.font-bold (tr [warning])])
       (into [:div.text-ui-night-sky.text-xs.leading-tight]
             (for [suggestion suggestions] [:div (tr [suggestion])]))]]]))


(defn password-visibility-button [tr password-visible? on-click hide?]
  [:button.bx--text-input--password__visibility__toggle.bx--btn.bx--btn--icon-only.bx--tooltip__trigger.bx--tooltip--top.bx--tooltip--align-end
   (p/assoc-if {:type       :button
                :aria-label (if password-visible? (tr [:profile/hide-password]) (tr [:profile/show-password]))
                :on-click   on-click}
               :tab-index (when hide? "-1"))
   [cc/icon {:icon        i/eye-off
             :description (tr [:profile/show-password])
             :fill        "currentcolor"
             :class-name  (str "icon--visibility-off" (when-not password-visible? " hidden"))}]
   [cc/icon {:icon        i/eye
             :description (tr [:profile/hide-password])
             :fill        "currentcolor"
             :class-name  (str "icon--visibility-on" (when password-visible? " hidden"))}]])


(defn invalid-password-text [invalid-text]
  [:div#input-password-error-msg.bx--form-requirement invalid-text])


(defn password-input
  [{:keys [tr id label use-meter? auto-focus? auto-complete class class-name light placeholder
           handle-change on-score-change invalid? invalid-text value default-value hide? ref]}]
  (r/with-let [*password-visible? (r/atom false)
               *score             (when use-meter? (r/atom nil))
               *input-ref         (r/atom nil)
               id                 (or id "input-password")]
    [:div.bx--form-item.bx--text-input-wrapper
     (p/assoc-if {:data-text-input ""
                  :class           (when @*password-visible? "bx--text-input--password-visible")
                  :style           {:height   (if hide? "0" "auto")
                                    :overflow (if hide? "hidden" "visible")
                                    :position (if hide? "absolute" "relative")}}
                 :aria-hidden hide?)
     (when-not hide?
       [:label.bx--label {:for id} label])
     [:div.bx--text-input__field-wrapper
      (when invalid? {:data-invalid invalid?})
      [:input.bx--text-input.bx--text-input--light.pr-8.relative
       (p/assoc-if
         {:type                            (if @*password-visible? :text :password)
          :id                              id
          :class                           (futil/cs class
                                                     class-name
                                                     (when light "bx--text-input--light"))
          :data-toggle-password-visibility ""
          :placeholder                     (or placeholder "")
          :auto-complete                   auto-complete
          :auto-focus                      auto-focus?
          :score                           (some-> *score deref :score :type)
          :on-change                       (fn [e]
                                             (let [v (futil/tval e)]
                                               (when use-meter?
                                                 (calculate-score
                                                   v
                                                   (fn [score]
                                                     (reset! *score score)
                                                     (when on-score-change
                                                       (on-score-change (-> score :score :type))))))
                                               (handle-change v)))}
         :value value
         :default-value (or default-value
                            (when-not value
                              ""))
         :ref #(do
                 (when (and (nil? @*input-ref) (some? %)) (reset! *input-ref %))
                 (when (some? ref) (ref %)))
         :data-invalid (when invalid? "true")
         :aria-invalid (when invalid? "true")
         :aria-describedby (when invalid? (str id "-error-msg"))
         :tab-index (when hide? "-1"))]
      [password-visibility-button tr @*password-visible? #(swap! *password-visible? not) hide?]]
     (when invalid?
       [invalid-password-text invalid-text])
     (when use-meter?
       [score-panel tr @*score])]))
